import { Link } from "react-router-dom";
import { CalendarDays, MapPin, ArrowUpRight, Star, Handshake, ExternalLink } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { InviteButton } from "./InviteDialog";
import { useLang } from "../../lib/i18n";
import { fmtDate } from "../../lib/api";
import { Img, classFallback, classesOf } from "./Img";

export const CapacityBadge = ({ cap }) => {
  const { t } = useLang();
  if (!cap?.max_participants) return null;
  if (cap.full) return <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white">{cap.waitlist_open ? t("waitlist") : t("full")}</span>;
  return <span className={`rounded-full px-3 py-1 text-xs font-bold ${cap.near_full ? "bg-[#FF5722] text-white" : "bg-white/90 text-[#0A2540]"}`}>{cap.remaining} {t("places_left")}</span>;
};

export const Exclusive = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-1 rounded-full bg-[#0A2540] px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-[#FFC107] ${className}`} data-testid="exclusive-badge"><Star size={13} fill="#FFC107" />EXCLUSIVE EVENT</span>
);

export const EventCard = ({ e, big = false }) => {
  const { tr, ts, lang, t } = useLang();
  const { user } = useAuth();
  const Wrap = e.partnership ? "a" : Link;
  const wrapProps = e.partnership ? { href: e.link, target: "_blank", rel: "noopener noreferrer" } : { to: `/clinics/${e.id}` };
  return (
    <Wrap {...wrapProps} data-testid={`event-card-${e.id}`} className={`card-lift img-zoom group relative flex flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200 ${big ? "md:row-span-2" : ""}`}>
      <div className={`relative overflow-hidden ${big ? "h-72 md:h-[420px]" : "h-56"}`}>
        <Img src={e.image} fallback={classFallback(classesOf(e)[0] || e.class_name)} alt={e.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/80 via-transparent to-transparent" />
        <div className="absolute left-4 top-4 flex max-w-[70%] flex-wrap gap-2">
          {e.partnership && <span className="inline-flex items-center gap-1 rounded-full bg-[#FFC107] px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-[#0A2540]" data-testid={`partnership-badge-${e.id}`}><Handshake size={13} />{t("partnership_badge")}</span>}
          {e.by_invitation && <Exclusive />}
          {classesOf(e).map((c) => <span key={c} className="rounded-full bg-[#00A8B5] px-3 py-1 text-xs font-bold text-white">{c}</span>)}
          {e.type && <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-[#0A2540]">{ts(e.type)}</span>}
        </div>
        {!e.partnership && <div className="absolute right-4 top-4"><CapacityBadge cap={e.capacity} /></div>}
        {e.by_invitation && user?.role === "admin" && <InviteButton e={e} />}
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <p className="flex items-center gap-2 text-sm font-semibold"><CalendarDays size={15} />{fmtDate(e.start_date, lang)} – {fmtDate(e.end_date, lang)}</p>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-6">
        <h3 className="font-display text-xl font-bold leading-tight text-[#0A2540]">{tr(e, "title")}</h3>
        <p className="flex items-center gap-1.5 text-sm text-slate-500"><MapPin size={14} className="text-[#00A8B5]" />{e.location?.name}</p>
        <div className="mt-auto flex items-center justify-between pt-2">
          <span className="text-xs font-bold uppercase tracking-wider text-[#FF5722]">{e.partnership ? t("partnership_open") : ts(e.status)}</span>
          {e.partnership ? <ExternalLink className="text-[#0A2540] transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" size={20} /> : <ArrowUpRight className="text-[#0A2540] transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" size={20} />}
        </div>
      </div>
    </Wrap>
  );
};
